import type { ThunkAction } from '@reduxjs/toolkit';

import type { UserProfile } from '../../survey-tool/declarations/auth-api';
import { browserNavigate } from '../../utils/browser-navigate';
import {
  goToAcceptTermsAndConditions,
  goToMobileNumberRequired,
  goToValidateEmail,
} from '../../utils/login-logout-uri';
import { redirectToCampaign } from '../../utils/redirects';
import * as AhApi from '../api/ah-api';
import type { RootState } from '../store';
import * as types from './action-types';
import { loadSurveyTheme } from './survey-theme-actions';

export function loadInstanceInfo(userProfile: UserProfile): ThunkAction<Promise<void>, RootState, void, any> {
  return async function (dispatch, getState) {
    const { panelKey } = userProfile;
    if (!panelKey) {
      throw new Error(`panelKey is not present on the userProfile`);
    }
    const instanceInfo = await AhApi.getInstanceInfoByPanelUuid(panelKey);
    const { panels } = instanceInfo;
    const panelInfo = panels[panelKey];
    if (!panelInfo) {
      throw new Error(`instanceInfo doesn't contain users panel ${panelKey}`);
    }

    // dispatch before doing any checks
    dispatch({ type: types.LOAD_INSTANCE_AND_PANEL_INFO_SUCCESS, instanceInfo, panelInfo });
    dispatch(loadSurveyTheme(panelInfo));

    // Only intercept from the feed — deep links to other pages (settings,
    // redemptions, surveys, etc.) keep working so users aren't bounced
    // mid-task.
    const onFeed = window.location.pathname === '/' || window.location.pathname.startsWith('/feed');

    const s = getState();
    if (onFeed && s.routeType !== 'any') {
      if (!userProfile.acceptedTermsConditions && panelInfo.app_settings.terms_required) {
        return goToAcceptTermsAndConditions(panelInfo, userProfile);
      }
      if (!userProfile.emailValidated && panelInfo.app_settings.email_validation_reminder) {
        return goToValidateEmail(panelInfo, userProfile);
      }
      if (
        panelInfo.app_settings.mobile_number_required &&
        !userProfile.verifiedMobileNumber &&
        !userProfile.unverifiedMobileNumber
      ) {
        return goToMobileNumberRequired(panelInfo, userProfile);
      }
    }

    // we need to check welcome campaigns, since welcome campaign might have been just completed
    if (s.routeType === 'private') {
      const response = await AhApi.getWelcomeCampaigns(userProfile);
      if (response.length > 0) {
        // some welcome campaigns have been found, redirect to it
        void redirectToCampaign(browserNavigate, response[0].guid);
      }
    }
  };
}
